import * as React from 'react';
import { css, FlattenSimpleInterpolation, ThemeContext } from 'styled-components';

import Box from '../Layout/Box';
import { VALID_DIMENSION_DATA } from '../Utils';

import CircleProgressLabel from './CircleProgressLabel';
import { BackgroundPath, PercentagePath, RadialProgressContainer } from './CircleProgressStyles';
import { IICircleProgressCommon } from './common';

const MIN_PERCENTAGE = 0;
const MAX_PERCENTAGE = 100;
const MAX_X = 100;
const MAX_Y = 100;
const FULL_RADIUS = 50;
const CENTER_X = 50;
const CENTER_Y = 50;

interface ICircleProgressProps extends IICircleProgressCommon {
  animate?: boolean;
  animationDelay?: string;
  size?: VALID_DIMENSION_DATA;
  strokeWidth?: number;
}

export const CircleProgress: React.FC<ICircleProgressProps> = ({
  animate,
  animationDelay= '1s',
  label,
  percentage,
  size,
  strokeWidth,
}) => {
  const RadialIconContainer = Box;
  let animationFrame: number | undefined;
  const [ isInitialized, setInitialized ] = React.useState(false);
  const [ pathDescription, setPathDescription ] = React.useState('');
  const { themeTwo: { circleProgress }} = React.useContext(ThemeContext);
  const circleStrokeWidth = strokeWidth ? strokeWidth : circleProgress.strokeWidth;

  /*
    the radius of the path is defined to be in the middle, so in order for the path to
    fit perfectly inside the 100x100 viewBox, need to subtract half the strokeWidth
  */
  const getPathRadius = (): number => FULL_RADIUS - circleStrokeWidth / 2;

  const getPathDescription = (): void => {
    const radius = getPathRadius();
    const rotation = 0;
    /*
      Move to center of canvas
      Relative move to top canvas
      Relative arc to bottom of canvas
      Relative arc to top of canvas
    */
    setPathDescription(`
      M ${CENTER_X},${CENTER_Y}
      m 0,-${radius}
      a ${radius},${radius} ${rotation} 1 1 0,${radius * 2}
      a ${radius},${radius} ${rotation} 1 1 0,-${radius * 2}
    `);
  };

  React.useEffect(() => {
    getPathDescription();

    if (animate) {
      animationFrame = window.requestAnimationFrame(() => {
        setInitialized(true);
      });
    }

    return () => {
      if (animationFrame) {
        window.cancelAnimationFrame(animationFrame);
      }
    };
  });

  const getPercentage = (): number => {
    if (animate && !isInitialized) {
      return 0;
    }

    return percentage;
  };

  const getPathStyles = (): FlattenSimpleInterpolation => {
    const diameter = Math.PI * 2 * getPathRadius();
    const truncatedPercentage = Math.min(
      Math.max(getPercentage(), MIN_PERCENTAGE),
      MAX_PERCENTAGE
    );
    const dashoffset = ((100 - truncatedPercentage) / 100) * diameter;

    return css`
      stroke-dasharray: ${`${diameter}px`};
      stroke-dashoffset: ${`${dashoffset}px`};
      transition: ${animate ? `all 600ms cubic-bezier(0.455, 0.03, 0.515, 0.955) ${animationDelay || 'initial'}` : 'initial'};
    `;
  };

  return (
    <RadialProgressContainer size={size}>
      <RadialIconContainer
        width='100%'
        height='100%'
        alignItems='center'
        justifyContent='center'
        direction='column'
      >
        <svg viewBox={`0 0 ${MAX_X} ${MAX_Y}`} aria-hidden={true}>
          <BackgroundPath
            d={pathDescription}
            strokeWidth={circleStrokeWidth}
            fillOpacity={0}
          />

          <PercentagePath
            d={pathDescription}
            fillOpacity={0}
            strokeWidth={circleStrokeWidth}
            strokeLinecap='round'
            animationStyles={getPathStyles()}
          />
        </svg>
        {label && <CircleProgressLabel percentage={percentage} label={label} />}
      </RadialIconContainer>
    </RadialProgressContainer>
  );
};

export default CircleProgress;
